import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgModule } from '@angular/core';
import {
  AddAppIDRequest,
  AppVendor,
  CreateVendorRequest,
  GetByPartnerIDRequest,
  GetMultiVendorRequest,
  GetOwnedAppIDsByPartnerIDRequest,
  IsVendorDistributedByPartnerIDRequest,
  IsVendorDistributedByPartnerIDResponse,
  ListVendorRequest,
  RemoveAppIDRequest,
  UpdateVendorRequest,
  VendorApiService,
} from '@vendasta/marketplace-apps/v1';
import { Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class VendorService {
  constructor(private apiService: VendorApiService) {}

  create(name: string): Observable<AppVendor | null> {
    const request = new CreateVendorRequest({ name: name });
    const response = this.apiService.create(request);
    return response.pipe(
      map((resp) => {
        return resp ? resp.vendor : null;
      }),
    );
  }
  list(
    cursor: string,
    pageSize: number,
  ): Observable<{
    vendors: AppVendor[];
    cursor: string;
    hasMore: boolean;
  }> {
    const request = new ListVendorRequest({ cursor: cursor, pageSize: pageSize });
    const response = this.apiService.list(request);
    return response.pipe(
      map((resp) => {
        if (!resp) {
          return { vendors: [], cursor: '', hasMore: false };
        }
        return { vendors: resp.vendors, cursor: resp.cursor, hasMore: resp.hasMore };
      }),
    );
  }
  getMulti(vendorIds: string[]): Observable<AppVendor[]> {
    const request = new GetMultiVendorRequest({ ids: vendorIds });
    const response = this.apiService.getMulti(request);
    return response.pipe(
      map((resp) => {
        if (resp && resp.vendors && resp.vendors.length > 0) {
          return resp.vendors;
        }
        return [];
      }),
    );
  }
  addAppId(vendorId: string, appId: string): Observable<AppVendor | null> {
    const request = new AddAppIDRequest({ id: vendorId, appId: appId });
    const response = this.apiService.addAppId(request);
    return response.pipe(
      map((resp) => {
        return resp ? resp.vendor : null;
      }),
    );
  }
  removeAppId(vendorId: string, appId: string): Observable<AppVendor | null> {
    const request = new RemoveAppIDRequest({ id: vendorId, appId: appId });
    const response = this.apiService.removeAppId(request);
    return response.pipe(
      map((resp) => {
        return resp ? resp.vendor : null;
      }),
    );
  }
  update(vendorId: string, name: string): Observable<AppVendor | null> {
    const request = new UpdateVendorRequest({ id: vendorId, name: name });
    const response = this.apiService.update(request);
    return response.pipe(
      map((resp) => {
        return resp ? resp.vendor : null;
      }),
    );
  }

  /*
      @params:
        - partnerId: represents the id of a partner to get the owned app ids for.
      @returns:
        - an observable of the string type
        - HttpErrorResponse with status 404 if partnerId is not present
        - HttpErrorResponse with status 500 if the api failed
        - HttpErrorResponse with 409 if the requester does not have access to the partner id
    */
  getOwnedAppIdsByPartnerId(partnerId: string | null): Observable<string[]> {
    if (!partnerId) {
      const e = new HttpErrorResponse({
        status: 404,
        statusText: 'partner id is required',
      });
      return throwError(e);
    }
    const request = new GetOwnedAppIDsByPartnerIDRequest({
      partnerId: partnerId,
    });
    return this.apiService.getOwnedAppIDsByPartnerId(request).pipe(map((resp) => (resp || { appIds: [] }).appIds));
  }

  /*
      @params:
        - appId: represents the id of an app to determine it's ownership of.
        - partnerId: represents the id of a partner to determine if it owns appId.
      @returns:
        - an observable of the string type
        - HttpErrorResponse with status 404 if partnerId is not present
        - HttpErrorResponse with status 500 if the api failed
        - HttpErrorResponse with 409 if the requester does not have access to the partner id
    */
  isAppOwnedByPartner(appID: string | null, partnerId: string | null): Observable<boolean> {
    if (!partnerId || !appID) {
      const e = new HttpErrorResponse({
        status: 404,
        statusText: 'partner id and app id is required',
      });
      return throwError(e);
    }

    return this.getOwnedAppIdsByPartnerId(partnerId).pipe(
      map((appIds) => {
        return appIds ? appIds.findIndex((i) => i === appID) > -1 : false;
      }),
    );
  }

  isVendorDistributed(partnerId: string): Observable<boolean | null> {
    const request = new IsVendorDistributedByPartnerIDRequest({ partnerId: partnerId });
    const response = this.apiService.isVendorDistributed(request);
    return response.pipe(
      map((resp: IsVendorDistributedByPartnerIDResponse) => {
        return resp ? resp.isDistributed : null;
      }),
    );
  }

  getByPartnerId(partnerId: string): Observable<AppVendor> {
    const request = new GetByPartnerIDRequest({ partnerId: partnerId });
    const response = this.apiService.getByPartnerId(request);
    return response.pipe(map((resp) => resp.vendor));
  }

  getVendorIdByPartnerId(partnerId: string): Observable<string> {
    return this.getByPartnerId(partnerId).pipe(map((vendor) => vendor.id));
  }
}

@NgModule({
  providers: [VendorApiService],
})
export class VendorServiceModule {}
